import { formatUserName } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { TableToolbar, TextTool } from "@impero-com/ui";
import { useOutletContext } from "react-router-dom";

import { AssignmentTable } from "org/user/components/AssignmentTable";
import { ReassignmentFailureAlert } from "org/user/components/ReassignmentFailureAlert";
import { RemoveUserModal } from "org/user/components/RemoveUserModal";
import { ReplaceUserModal } from "org/user/components/ReplaceUserModal";
import { useEntitiesForUserQuery } from "org/user/queries/useEntitiesForUserQuery";
import { OutletContext } from "org/user/UserViewPage";
import { getFilterModeConfig } from "utils/table/getFilterModeConfig";

import { describeEntityFailure } from "./describeEntityFailure";
import { useRemoveEntityAccessRightUtils } from "./useRemoveEntityAccessRightUtils";
import { useReplaceEntityUserUtils } from "./useReplaceEntityUserUtils";
import {
    EntityRow,
    useUserEntitiesListTable,
} from "./useUserEntitiesListTable";

export function UserEntitiesList() {
    const { user } = useOutletContext<OutletContext>();
    const userId = user.id;

    const { data: entities } = useEntitiesForUserQuery(userId);
    const { t } = useTranslation();

    const { table, getFilterComponent, tableId } =
        useUserEntitiesListTable(entities);

    const modeConfig = getFilterModeConfig(t);

    const selectedRows = table.getFilteredSelectedRowModel().rows;
    const deselectAll = () => table.resetRowSelection();

    const {
        removeAccessRightToolState,
        isRemoveAccessRightModalOpen,
        removeAccessRightError,
        openRemoveAccessRightModal,
        closeRemoveAccessRightModal,
        removeAccessRight,
        isRemovingAccessRight,
    } = useRemoveEntityAccessRightUtils({ userId, selectedRows, deselectAll });

    const {
        replaceUserToolState,
        isReplaceUserModalOpen,
        replaceUserError,
        openReplaceUserModal,
        closeReplaceUserModal,
        replaceUser,
        isReplacingUser,
    } = useReplaceEntityUserUtils({ userId, selectedRows, deselectAll });

    const removeFailure = describeEntityFailure(
        t,
        removeAccessRightError,
        entities
    );
    const replaceFailure = describeEntityFailure(t, replaceUserError, entities);

    return (
        <div>
            <RemoveUserModal
                onClose={closeRemoveAccessRightModal}
                onSubmit={removeAccessRight}
                open={isRemoveAccessRightModalOpen}
                loading={isRemovingAccessRight}
                message={t("AccessAndAssignment_RemoveEntityDialog_Text", {
                    user: formatUserName(user),
                    count: selectedRows.length,
                })}
                error={
                    removeFailure && (
                        <ReassignmentFailureAlert failure={removeFailure} />
                    )
                }
            />
            <ReplaceUserModal
                open={isReplaceUserModalOpen}
                replaceType="WithUser"
                loading={isReplacingUser}
                onClose={closeReplaceUserModal}
                onClick={replaceUser}
                user={user}
                error={
                    replaceFailure && (
                        <ReassignmentFailureAlert failure={replaceFailure} />
                    )
                }
            />
            <AssignmentTable<EntityRow>
                table={table}
                tableId={tableId}
                getFilterComponent={getFilterComponent}
                modeConfig={modeConfig}
                toolbar={
                    <TableToolbar>
                        <TextTool
                            state={removeAccessRightToolState}
                            onClick={openRemoveAccessRightModal}
                        >
                            {t(
                                "AccessAndAssignment_Tool_RemoveUserFromEntities"
                            )}
                        </TextTool>
                        <TextTool
                            state={replaceUserToolState}
                            onClick={openReplaceUserModal}
                        >
                            {t("AccessAndAssignment_Tool_ReplaceUser")}
                        </TextTool>
                    </TableToolbar>
                }
            />
        </div>
    );
}
